import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { ArrowLeft, Salad } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PlanView } from "@/components/diet/PlanView";
import { getMyDietPlans } from "@/lib/diet/diet-plan.functions";
import { TRACKS, type DietTrack, type GeneratedPlan } from "@/lib/diet/plan";

export const Route = createFileRoute("/_authenticated/account_/plans")({
  head: () => ({
    meta: [
      { title: "Your diet plans · TruWe" },
      { name: "description", content: "Your saved TruWe diet plans and daily routines." },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: PlansPage,
});

type Row = { id: string; track: string; plan: GeneratedPlan; created_at: string };

function PlansPage() {
  const fetchPlans = useServerFn(getMyDietPlans);
  const { data, isLoading } = useQuery({
    queryKey: ["my-diet-plans"],
    queryFn: () => fetchPlans({}),
  });
  const [openId, setOpenId] = useState<string | null>(null);

  const plans = (data?.plans ?? []) as unknown as Row[];
  const active = plans.find((p) => p.id === openId);

  return (
    <main className="mx-auto max-w-5xl px-4 py-10">
      <Link
        to="/account"
        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" /> Back to account
      </Link>

      {active ? (
        <div className="mt-6">
          <Button
            variant="outline"
            className="mb-6 rounded-full print:hidden"
            onClick={() => setOpenId(null)}
          >
            All plans
          </Button>
          <PlanView plan={active.plan} track={active.track as DietTrack} />
        </div>
      ) : (
        <>
          <h1 className="mt-4 font-display text-2xl font-semibold">Your diet plans</h1>
          {isLoading ? (
            <p className="mt-4 text-sm text-muted-foreground">Loading…</p>
          ) : plans.length === 0 ? (
            <div className="mt-6 rounded-3xl border border-border bg-card p-8 text-center">
              <Salad className="mx-auto h-8 w-8 text-brand" />
              <p className="mt-3 text-sm text-muted-foreground">
                You haven't generated a plan yet.
              </p>
              <Button asChild className="mt-4 rounded-full">
                <Link to="/diet-plan">Build a free plan</Link>
              </Button>
            </div>
          ) : (
            <ul className="mt-6 grid gap-3 sm:grid-cols-2">
              {plans.map((p) => (
                <li key={p.id}>
                  <button
                    type="button"
                    onClick={() => setOpenId(p.id)}
                    className="w-full rounded-2xl border border-border bg-card p-5 text-left transition hover:border-brand/40"
                  >
                    <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-brand">
                      {TRACKS[p.track as DietTrack]?.label ?? p.track}
                    </span>
                    <span className="mt-1 block font-display text-base font-semibold">
                      {p.plan?.headline ?? "7-day plan"}
                    </span>
                    <span className="mt-1 block text-xs text-muted-foreground">
                      {new Date(p.created_at).toLocaleDateString("en-IN", {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                      })}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </main>
  );
}
